import Joi from "joi";
import { useEffect, useState } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { connect } from "react-redux";
import {
  getUniversities,
  getCollegeAdmins,
  getCollegeTypes,
  getCollegeName,
} from "../../../../../action/common/commonActionCreator";
import { createCollege } from "../../../../../action/masters/collegeActionCreator";
import FloatingLabelDropDown from "../../../../../common-components/floatingdropdown";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import { validateFields } from "../../../../../helper/joi-validation";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import {
  ICreateCollege,
  ICollegeData,
} from "../../../../../models/master/college";
import LoaderSpinner from "../../../../../common-components/loader";
import { sortAlphabetically } from "../../../../../common-functions/commonFunctions";
import { collegeType } from "../../../../../constants/collegeType";
import { maxImageSize } from "../../../../../constants/commonConstants";

const CollegeCreate = (props: ICreateCollege) => {
  const {
    createCollege,
    setShowCreate,
    getUniversities,
    universities,
    getAllColleges,
    loginUsers,
    getLoginUsers,
    getCollegeTypes,
    collegeTypes,
    getCollegeName,
    collegeName,
  } = props;

  const initialData: ICollegeData = {
    id: null,
    collegeName: "",
    externalId: 0,
    file: null,
    description: "",
    isActive: true,
    universityId: 0,
    address: "",
    location: "",
    collegeLoginUserId: "",
    collegeLoginUserName: "",
    collegeType: 0,
    collegeTypeName: "",
  };

  // states

  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [name, setName] = useState<string>("");
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [imageName, setImageName] = useState<string | null>(null);

  // change the data at the time onchange fields
  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    let _data = { ...data, [event.target.name]: event.target.value };

    if (event.target.name === "collegeType") {
      getCollegeName(Number(event.target.value));
    }

    if (event.target.name === "collegeName") {
      let name = collegeName.find(
        (college) => college.id === event.target.value
      );
      setName(name?.name);
      _data.collegeName = event.target.value;
    }

    errors && delete errors[event.target.name];
    setData({ ...data, [event.target.name]: event.target.value });
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    errors && delete errors[event.target.name];
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setImageName(selectedFile.name);
      const fileType = selectedFile.type;
      const fileSize = selectedFile.size;
      const validFileTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/ico",
        "image/svg+xml",
      ];
      if (!validFileTypes.includes(fileType)) {
        setErrors({
          ...errors,
          file: "Invalid file type. Please upload an image.",
        });
      } else if (fileSize > maxImageSize) {
        setErrors({
          ...errors,
          file: "File size exceeds 1MB. Please upload a small file.",
        });
        setImagePreview(null);
        setImageName(null);
      } else {
        setData({ ...data, [event.target.name]: selectedFile });
        const fileURL = URL.createObjectURL(selectedFile);
        setImagePreview(fileURL);
      }
    }
  };

  const schema = Joi.object().keys({
    collegeName: Joi.number()
      .greater(0)
      .required()
      .label("Select University Management"),
    universityId: Joi.number().greater(0).required().label("Select University"),
    description: Joi.string().required().label("Description is required"),
    location: Joi.string().required().label("Location is required"),
    address: Joi.string().required().label("Address is required"),
    file: Joi.object().required().label("Logo is required"),
    collegeType: Joi.number()
      .greater(0)
      .required()
      .label("Select Management Type"),
  });

  const onSave = async () => {
    const validationErrors = validateFields(data, schema);
    if (validationErrors === undefined) {
      const selectedUser = loginUsers.find(
        (user: any) => user.id === data.collegeLoginUserId
      );
      if (selectedUser) {
        data.collegeLoginUserName = selectedUser.name;
      }

      const formData = new FormData();
      formData.append("id", data.id?.toString() || "");
      formData.append("collegeName", name);
      formData.append("externalId", data.collegeName);
      formData.append("file", data.file);
      formData.append("description", data.description.replace(/\s+$/g, ""));
      formData.append("address", data.address);
      formData.append("location", data.location);
      formData.append("universityId", data.universityId.toString() || "");
      formData.append(
        "collegeLoginUserId",
        data.collegeLoginUserId.toString() || ""
      );
      formData.append("collegeLoginUserName", data.collegeLoginUserName || "");
      formData.append("collegeType", data.collegeType.toString() || "");
      formData.append("isActive", data.isActive.toString());
      setLoading(true);
      setIsDisabled(true);
      try {
        await createCollege(formData, setAlert);
      } finally {
        setLoading(false);
        setIsDisabled(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  // setting sweet alert dynamically
  const setAlert = (type: string, name?: string) => {
    let alertData = getAlertData(type, name);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      getAllColleges();
      setShowCreate(false);
    }
  };

  useEffect(() => {
    getUniversities();
    getLoginUsers();
    getCollegeTypes();
    // getCollegeName();
  }, []);

  useEffect(() => {
    if (universities) {
      setData((prevData) => ({
        ...prevData,
        universityId: universities[0].id,
      }));
    }
  }, [universities]);

  return (
    <>
      <div>
        <div className="modal-header">
          <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
            Add New University Management
          </h1>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={() => setShowCreate(false)}
          ></button>
        </div>
        <div className="modal-body">
          <div className="row">
            <div className="col-md-12 form-group">
              <FloatingLabelDropDown
                label={"University "}
                onChange={onChange}
                isMandatory={true}
                value={data.universityId}
                name={"universityId"}
                optionsList={sortAlphabetically(universities)}
                disable
              />
              {errors?.universityId && (
                <div className="text-danger">{errors.universityId}</div>
              )}
            </div>
            <div className="col-md-6 form-group">
              <FloatingLabelDropDown
                label={"Management Type"}
                onChange={onChange}
                isMandatory={true}
                value={data.collegeType}
                name={"collegeType"}
                optionsList={sortAlphabetically(collegeTypes)}
              />
              {errors?.collegeType && (
                <div className="text-danger">{errors.collegeType}</div>
              )}
            </div>
            <div className="col-md-6 form-group">
              <FloatingLabelDropDown
                label={"University Management Name "}
                onChange={onChange}
                isMandatory={true}
                value={data.collegeName}
                name={"collegeName"}
                optionsList={sortAlphabetically(collegeName || [])}
              />
              {errors?.collegeName && (
                <div className="text-danger">{errors.collegeName}</div>
              )}
            </div>
            <div className="col-md-6 form-group">
              <FloatingLabelTextInput
                label={"Address "}
                onChange={onChange}
                isMandatory={true}
                value={data.address}
                name={"address"}
                maxLength={200}
              />
              {errors?.address && (
                <div className="text-danger">{errors.address}</div>
              )}
            </div>
            <div className="col-md-6 form-group">
              <FloatingLabelTextInput
                label={"Location "}
                onChange={onChange}
                isMandatory={true}
                value={data.location}
                name={"location"}
                maxLength={50}
              />
              {errors?.location && (
                <div className="text-danger">{errors.location}</div>
              )}
            </div>
            <div className="col-md-6 form-group">
              <label className="profile_details_text">
                University Management Logo<i className="label-asterisk"> * </i>
              </label>
              <div className="inputfile-flex">
                {imagePreview && (
                  <div className="inputfile-img">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{ width: "50px", height: "50px" }}
                    />
                  </div>
                )}
                <div className="inputfile-wrap">
                  <input
                    type="file"
                    name="file"
                    id="image"
                    className="form-control d-none"
                    required
                    onChange={onFileChange}
                    accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/webp, image/x-icon, text/html"
                    value={errors?.file && ""}
                  />
                  <div className="inputfile">
                    <label htmlFor="image">Choose File</label>
                    <span>
                      {imageName
                        ? imageName.trim().split("/").pop()
                        : "No File Choosen"}
                    </span>
                  </div>
                </div>
              </div>
              {errors?.file && <div className="text-danger">{errors.file}</div>}
            </div>
            <div className="col-md-6 form-group">
              <FloatingLabelDropDown
                label={"Login user ID "}
                onChange={onChange}
                isMandatory={false}
                value={data?.collegeLoginUserId}
                name={"collegeLoginUserId"}
                optionsList={sortAlphabetically(loginUsers || [])}
                disable={
                  data?.collegeType == collegeType.universityAdministration
                }
              />
              {errors?.collegeLoginUserId && (
                <div className="text-danger">{errors.collegeLoginUserId}</div>
              )}
            </div>
            <div className="col-md-12 form-group">
              <label className="profile_details_text">
                Description<i className="label-asterisk"> * </i>
              </label>
              <textarea
                name="description"
                value={data.description}
                onChange={onChange}
                maxLength={1000}
              ></textarea>
              {errors?.description && (
                <div className="text-danger">{errors.description}</div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSave}
              disabled={isDisabled}
            >
              Save
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              data-bs-dismiss="modal"
              onClick={() => setShowCreate(false)}
              disabled={isDisabled}
            >
              Close
            </button>
          </div>
        </div>
      </div>
      <SweetAlert
        show={showAlert}
        success={false}
        type={alertType}
        title={alertTitle}
        onConfirm={() => onConfirm()}
        showCancel={showAlertCancel}
        onCancel={() => {
          setShowAlert(false);
          setShowAlertCancel(false);
        }}
        cancelBtnText={"No"}
        confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
      ></SweetAlert>
      {loading && <LoaderSpinner />}
    </>
  );
};

const mapDispatchToProps = {
  createCollege: (data: any, callBack: Function) =>
    createCollege(data, callBack),
  getUniversities: () => getUniversities(),
  getLoginUsers: () => getCollegeAdmins(),
  getCollegeTypes: () => getCollegeTypes(),
  getCollegeName: (userType: number) => getCollegeName(userType),
};

function mapStateToProps(state: any) {
  return {
    universities: state.common.universities,
    loginUsers: state.common.loginUsers,
    collegeTypes: state.common.collegeTypes,
    collegeName: state.common.collegeName,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(CollegeCreate);
